import React, { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ReceiptText, AlertCircle, Search, CheckCircle2, Printer, X } from "lucide-react";
import { toast } from "sonner";
import MarkPaidDialog from "@/pages/owner/employees/MarkPaidDialog";
import PayslipHistoryDialog from "@/pages/owner/employees/PayslipHistoryDialog";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_TYPE_BADGE = {
  normal_working_day: "bg-[#E3F2FD] text-[#1565C0]",
  regular_holiday: "bg-[#FFF3E0] text-[#E65100]",
  special_holiday: "bg-[#F3E5F5] text-[#6A1B9A]",
};
const ATTENDANCE_BADGE = {
  work_day: "bg-[#E8F5E9] text-[#2E7D32]",
  rest_day: "bg-[#E3F2FD] text-[#1565C0]",
  leave_with_pay: "bg-[#ECEFF1] text-[#37474F]",
  leave_without_pay: "bg-[#FFEBEE] text-[#C62828]",
  absent: "bg-[#FFEBEE] text-[#C62828]",
  training_day: "bg-[#F3E5F5] text-[#6A1B9A]",
};

export default function PaySlipPage() {
  const { selectedSchool, selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const { user } = useAuth();
  const isMonthlyFreq = (user?.salary_frequency || "twice_monthly") === "monthly";
  const currency = user?.currency_symbol || "₱";

  const today = new Date();
  const [employeeId, setEmployeeId] = useState("");
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [period, setPeriod] = useState(isMonthlyFreq ? "monthly" : "second_half");
  const [search, setSearch] = useState("");

  const [employees, setEmployees] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // Keep period in sync with frequency
  useEffect(() => {
    if (isMonthlyFreq && period !== "monthly") setPeriod("monthly");
    if (!isMonthlyFreq && period === "monthly") setPeriod("second_half");
  }, [isMonthlyFreq, period]);

  // Load employees of selected branch
  useEffect(() => {
    if (!selectedBranch) { setEmployees([]); setEmployeeId(""); return; }
    (async () => {
      try {
        const { data } = await api.get("/employees", { params: { branch_id: selectedBranch.id } });
        setEmployees(data || []);
        if (data?.length && !data.find((e) => e.id === employeeId)) setEmployeeId(data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch?.id]);

  const loadPreview = useCallback(async () => {
    if (!selectedBranch || !employeeId) { setPreview(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/payslip/preview", {
        params: { branch_id: selectedBranch.id, employee_id: employeeId, year, month, period },
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, employeeId, year, month, period]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const yearOptions = useMemo(() => {
    const y = today.getFullYear();
    const out = [];
    for (let i = y - 3; i <= y + 1; i += 1) out.push(i);
    return out;
  }, [today]);

  const filteredDaily = useMemo(() => {
    if (!preview) return [];
    const q = search.trim().toLowerCase();
    if (!q) return preview.daily;
    return preview.daily.filter((d) =>
      d.date.includes(q) ||
      d.weekday.toLowerCase().includes(q) ||
      d.day_type_label.toLowerCase().includes(q) ||
      d.attendance_label.toLowerCase().includes(q)
    );
  }, [preview, search]);

  return (
    <>
      <div className="mb-6 flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#E3F2FD] flex items-center justify-center">
          <ReceiptText className="w-6 h-6 text-[#1976D2]" />
        </div>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Pay Slip</h1>
          <p className="text-brand-inkMuted mt-0.5">Generate employee attendance and payroll summary.</p>
        </div>
      </div>

      {!selectedBranch ? (
        <NoBranchCard />
      ) : (
        <>
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4 mb-5" data-testid="payslip-filters">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Choose Branch">
                <select
                  value={selectedBranch.id}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  className="ps-select"
                  data-testid="payslip-branch-select"
                >
                  {branchesForSelectedSchool.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Choose Employee">
                <select
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  className="ps-select"
                  data-testid="payslip-employee-select"
                >
                  {employees.length === 0 && <option value="">— No employees —</option>}
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </Field>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <Field label="Choose Month">
                <select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="ps-select"
                  data-testid="payslip-month-select"
                >
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </Field>
              <Field label="Choose Pay Period">
                {isMonthlyFreq ? (
                  <div className="ps-select flex items-center text-foreground" data-testid="payslip-period-fixed">Monthly</div>
                ) : (
                  <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    className="ps-select"
                    data-testid="payslip-period-select"
                  >
                    <option value="first_half">1st Half: 1–15</option>
                    <option value="second_half">2nd Half: 16–End of Month</option>
                  </select>
                )}
              </Field>
              <Field label="Choose Year">
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="ps-select"
                  data-testid="payslip-year-select"
                >
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </Field>
            </div>
          </section>

          {loading ? (
            <p className="text-brand-inkMuted">Loading...</p>
          ) : !preview || !employeeId ? (
            <EmptyState />
          ) : (
            <>
              <div className="relative mb-6">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-brand-inkMuted" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search attendance records..."
                  className="rounded-2xl h-11 pl-10 border-brand-purple/15 bg-white"
                  data-testid="payslip-search"
                />
              </div>

              <SummaryGrid summary={preview.summary} />

              <DailyTable rows={filteredDaily} />

              <BreakdownSection
                preview={preview}
                currency={currency}
                branchId={selectedBranch.id}
                employeeId={employeeId}
                year={year}
                month={month}
                period={period}
                school={selectedSchool}
                branch={selectedBranch}
                user={user}
                filteredDaily={filteredDaily}
              />
            </>
          )}
        </>
      )}
      <style>{`
        .ps-select {
          width: 100%;
          height: 2.25rem;
          border-radius: 0.875rem;
          border: 1px solid rgba(37, 99, 235, 0.18);
          background: #ffffff;
          padding: 0 0.625rem;
          font-size: 0.875rem;
          color: hsl(var(--foreground));
        }
        .ps-select:focus-visible { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring)); }
      `}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-heading text-brand-inkMuted">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SummaryCard({ title, children, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 h-full flex flex-col" data-testid={testId}>
      <p className="text-center font-heading text-[10px] sm:text-[11px] font-semibold text-brand-inkMuted tracking-wider uppercase mb-2 min-h-[2.25rem] flex items-center justify-center leading-tight">{title}</p>
      <div className="flex-1 flex items-center justify-center">{children}</div>
    </div>
  );
}

function TwoColMetric({ leftLabel, leftValue, rightLabel, rightValue, testId }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-center w-full" data-testid={testId}>
      <div>
        <p className="text-[9px] font-heading text-brand-inkMuted tracking-wider">{leftLabel}</p>
        <p className="font-heading text-xl font-semibold text-foreground mt-0.5">{leftValue}</p>
      </div>
      <div className="border-l border-brand-purple/10">
        <p className="text-[9px] font-heading text-brand-inkMuted tracking-wider">{rightLabel}</p>
        <p className="font-heading text-xl font-semibold text-foreground mt-0.5">{rightValue}</p>
      </div>
    </div>
  );
}

function OneMetric({ value, testId }) {
  return (
    <div className="w-full text-center">
      {/* Spacer keeps the number on the same baseline as the two-column cards */}
      <p className="text-[9px] font-heading text-brand-inkMuted tracking-wider invisible">&nbsp;</p>
      <p className="font-heading text-xl font-semibold text-foreground mt-0.5" data-testid={testId}>{value}</p>
    </div>
  );
}

function SummaryGrid({ summary }) {
  return (
    <div className="space-y-4 mb-6" data-testid="payslip-summary">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard title="Total Days" testId="card-total-days">
          <TwoColMetric leftLabel="Total" leftValue={summary.total_days} rightLabel="Worked Full Shift" rightValue={summary.worked_full_shift} />
        </SummaryCard>
        <SummaryCard title="Normal Working Days" testId="card-normal">
          <TwoColMetric leftLabel="Total" leftValue={summary.normal_total} rightLabel="Worked Full Shift" rightValue={summary.normal_worked} />
        </SummaryCard>
        <SummaryCard title="Regular Holidays" testId="card-regular-holiday">
          <TwoColMetric leftLabel="Total" leftValue={summary.regular_holiday_total} rightLabel="Worked Full Shift" rightValue={summary.regular_holiday_worked} />
        </SummaryCard>
        <SummaryCard title="Special Holidays" testId="card-special-holiday">
          <TwoColMetric leftLabel="Total" leftValue={summary.special_holiday_total} rightLabel="Worked Full Shift" rightValue={summary.special_holiday_worked} />
        </SummaryCard>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard title="Total Days with Late (Deduction)"><OneMetric value={summary.late_days} testId="card-late" /></SummaryCard>
        <SummaryCard title="Total Days Absent"><OneMetric value={summary.absent_days} testId="card-absent" /></SummaryCard>
        <SummaryCard title="Total Days Vacation Leave With Pay"><OneMetric value={summary.leave_with_pay_days} testId="card-lwp" /></SummaryCard>
        <SummaryCard title="Days Vacation Leave Without Pay"><OneMetric value={summary.leave_without_pay_days} testId="card-lwop" /></SummaryCard>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard title="Days with Rest Day">
          <TwoColMetric leftLabel="Total" leftValue={summary.rest_day_total} rightLabel="Worked" rightValue={summary.rest_day_worked} />
        </SummaryCard>
        <SummaryCard title="Normal Working with Rest Days">
          <TwoColMetric leftLabel="Total" leftValue={summary.normal_rest_total} rightLabel="Worked" rightValue={summary.normal_rest_worked} />
        </SummaryCard>
        <SummaryCard title="Regular Holidays (Rest)">
          <TwoColMetric leftLabel="Total" leftValue={summary.regular_holiday_rest_total} rightLabel="Worked" rightValue={summary.regular_holiday_rest_worked} />
        </SummaryCard>
        <SummaryCard title="Special Holidays (Rest)">
          <TwoColMetric leftLabel="Total" leftValue={summary.special_holiday_rest_total} rightLabel="Worked" rightValue={summary.special_holiday_rest_worked} />
        </SummaryCard>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard title="Total Overtime Hours"><OneMetric value={summary.overtime_total} testId="card-ot-total" /></SummaryCard>
        <SummaryCard title="Overtime in Normal Working Days"><OneMetric value={summary.overtime_normal} testId="card-ot-normal" /></SummaryCard>
        <SummaryCard title="Overtime in Regular Holidays"><OneMetric value={summary.overtime_regular_holiday} testId="card-ot-rh" /></SummaryCard>
        <SummaryCard title="Overtime in Special Holidays"><OneMetric value={summary.overtime_special_holiday} testId="card-ot-sh" /></SummaryCard>
      </div>
    </div>
  );
}

function DailyTable({ rows }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden mb-5" data-testid="payslip-daily-table">
      <table className="w-full text-xs sm:text-sm table-fixed">
        <colgroup>
          <col className="w-[18%]" />
          <col className="w-[13%]" />
          <col className="w-[13%]" />
          <col className="w-[12%]" />
          <col className="w-[16%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
        </colgroup>
        <thead className="bg-[#F1F5FF]">
          <tr className="text-brand-inkMuted">
            <th className="text-left px-3 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">DATE / DAY</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">DAY TYPE</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">ATTENDANCE TYPE</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px] leading-tight">MINUTES LATE</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px] leading-tight">TOTAL HOURS WORKED FULL SHIFT</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">OVERTIME</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">UNDERTIME</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-5 text-center text-brand-inkMuted">No attendance records in this period.</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.date} className={i % 2 ? "bg-white" : "bg-[#FAFBFE]"} data-testid={`payslip-row-${r.date}`}>
              <td className="px-3 py-2 font-body text-foreground text-xs sm:text-sm">
                {formatRowDate(r.date)}<span className="hidden sm:inline"> - {r.weekday}</span>
              </td>
              <td className="px-2 py-2 text-center">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold ${DAY_TYPE_BADGE[r.day_type] || "bg-brand-purpleLight text-foreground"}`}>
                  {r.day_type_label}
                </span>
              </td>
              <td className="px-2 py-2 text-center">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold ${ATTENDANCE_BADGE[r.attendance_type] || "bg-brand-purpleLight text-foreground"}`}>
                  {r.attendance_label}
                </span>
              </td>
              <td className="px-2 py-2 text-center font-body">{r.minutes_late}</td>
              <td className="px-2 py-2 text-center font-body">{r.hours_worked}</td>
              <td className="px-2 py-2 text-center font-body">{r.overtime}</td>
              <td className="px-2 py-2 text-center font-body">{r.undertime}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatRowDate(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function BreakdownSection({ preview, currency, branchId, employeeId, year, month, period, school, branch, user, filteredDaily }) {
  const { breakdown, has_formula } = preview;
  const incomes = breakdown.incomes || [];
  const deductions = breakdown.deductions || [];
  const otherDeductions = breakdown.other_deductions || [];

  const [manualAddAmount, setManualAddAmount] = useState("");
  const [manualAddReason, setManualAddReason] = useState("");
  const [manualDedAmount, setManualDedAmount] = useState("");
  const [manualDedReason, setManualDedReason] = useState("");
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjSaving, setAdjSaving] = useState(false);
  const isInitialLoad = React.useRef(true);

  // Load persisted adjustment whenever the employee/period changes
  useEffect(() => {
    if (!branchId || !employeeId) return;
    let cancelled = false;
    isInitialLoad.current = true;
    setAdjLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/payslip/manual-adjustment", {
          params: { branch_id: branchId, employee_id: employeeId, year, month, period },
        });
        if (cancelled) return;
        setManualAddAmount(Number(data.add_amount) > 0 ? String(data.add_amount) : "");
        setManualAddReason(data.add_reason || "");
        setManualDedAmount(Number(data.ded_amount) > 0 ? String(data.ded_amount) : "");
        setManualDedReason(data.ded_reason || "");
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) {
          setAdjLoading(false);
          // Allow a tick before enabling the save effect to avoid an immediate echo PUT
          setTimeout(() => { isInitialLoad.current = false; }, 50);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [branchId, employeeId, year, month, period]);

  // Debounced auto-save on any change
  useEffect(() => {
    if (!branchId || !employeeId) return;
    if (adjLoading || isInitialLoad.current) return;
    const t = setTimeout(async () => {
      setAdjSaving(true);
      try {
        await api.put("/payslip/manual-adjustment", {
          branch_id: branchId,
          employee_id: employeeId,
          year, month, period,
          add_amount: Number(manualAddAmount) || 0,
          add_reason: manualAddReason || "",
          ded_amount: Number(manualDedAmount) || 0,
          ded_reason: manualDedReason || "",
        });
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setAdjSaving(false); }
    }, 700);
    return () => clearTimeout(t);
  }, [manualAddAmount, manualAddReason, manualDedAmount, manualDedReason,
      branchId, employeeId, year, month, period, adjLoading]);

  const manualAdd = Number(manualAddAmount) || 0;
  const manualDed = Number(manualDedAmount) || 0;

  const adjustedGross = breakdown.gross_pay + manualAdd;
  const adjustedDeductions = breakdown.module_deductions + breakdown.other_deductions_total + manualDed;
  const adjustedNet = adjustedGross - adjustedDeductions;

  // Mark-as-Paid status
  const [paidStatus, setPaidStatus] = useState(null); // null = unknown, {} when paid
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);

  const refreshPaidStatus = useCallback(async () => {
    if (!branchId || !employeeId) { setPaidStatus(null); return; }
    try {
      const { data } = await api.get("/payslip/salary-disbursement", {
        params: { branch_id: branchId, employee_id: employeeId, year, month, period },
      });
      setPaidStatus(data || null);
    } catch (e) { /* silent */ }
  }, [branchId, employeeId, year, month, period]);

  useEffect(() => { refreshPaidStatus(); }, [refreshPaidStatus]);

  const employeeName = preview?.employee?.name || "Employee";
  const isPaid = !!paidStatus;

  return (
    <section className="space-y-4 mb-5" data-testid="payslip-breakdown">
      <div>
        <h2 className="font-heading text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
          Standard Salary Calculation with Breakdown
        </h2>
        <p className="text-brand-inkMuted text-xs sm:text-sm mt-0.5">
          Computation based on Employee Salary Module rates, Employee Salary Formula multipliers, and attendance records.
        </p>
      </div>

      <DailyIncomeBreakdownTable preview={preview} currency={currency} />

      {!has_formula && incomes.every((i) => i.schedule !== "Calculated Daily") && incomes.length === 0 && (
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-6 text-brand-inkMuted" data-testid="no-formula-empty">
          No salary formula found. Please set up Employee Salary Formula.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4" data-testid="breakdown-incomes">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm sm:text-base font-semibold text-[#2E7D32] tracking-wider">INCOME</h3>
            <span className="font-heading font-semibold text-[#2E7D32] text-sm sm:text-base">
              {currency} {breakdown.total_income.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          {incomes.length === 0 ? (
            <p className="text-xs text-brand-inkMuted">No income items apply to this period.</p>
          ) : (
            <ul className="space-y-1">
              {incomes.map((i) => (
                <li key={i.label_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-brand-purple/5 last:border-0">
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-foreground text-xs sm:text-sm">{i.label}</p>
                    <p className="text-[10px] sm:text-[11px] text-brand-inkMuted">{i.schedule}</p>
                    {i.late_deduction > 0 && (
                      <p className="text-[10px] text-[#C62828]">Late deduction: {currency} {i.late_deduction.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    )}
                  </div>
                  <span className="font-body text-foreground text-xs sm:text-sm whitespace-nowrap">{currency} {i.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </li>
              ))}
              {breakdown.total_overtime > 0 && (
                <li className="flex items-center justify-between gap-2 py-1.5 border-t border-brand-purple/10 mt-2" data-testid="breakdown-overtime-line">
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-foreground text-xs sm:text-sm">Overtime Pay</p>
                    <p className="text-[10px] sm:text-[11px] text-brand-inkMuted">Across all daily columns</p>
                  </div>
                  <span className="font-body text-foreground text-xs sm:text-sm whitespace-nowrap">
                    {currency} {breakdown.total_overtime.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </li>
              )}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4" data-testid="breakdown-deductions">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm sm:text-base font-semibold text-[#C62828] tracking-wider">DEDUCTION</h3>
            <span className="font-heading font-semibold text-[#C62828] text-sm sm:text-base">
              {currency} {(breakdown.module_deductions + breakdown.other_deductions_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          {deductions.length === 0 ? (
            <p className="text-xs text-brand-inkMuted">No deduction items apply to this period.</p>
          ) : (
            <ul className="space-y-1">
              {deductions.map((d) => (
                <li key={d.label_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-brand-purple/5 last:border-0">
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-foreground text-xs sm:text-sm">{d.label}</p>
                    <p className="text-[10px] sm:text-[11px] text-brand-inkMuted">{d.schedule}</p>
                  </div>
                  <span className="font-body text-foreground text-xs sm:text-sm whitespace-nowrap">{currency} {d.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </li>
              ))}
            </ul>
          )}
          {otherDeductions.length > 0 && (
            <ul className="space-y-1 mt-2 pt-2 border-t border-brand-purple/10" data-testid="payslip-other-deductions">
              <li className="text-[10px] font-heading font-semibold uppercase tracking-wider text-brand-purple mb-1">Other deductions</li>
              {otherDeductions.map((o) => (
                <li key={o.deduction_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-brand-purple/5 last:border-0">
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-foreground text-xs sm:text-sm">{o.name}</p>
                    <p className="text-[10px] sm:text-[11px] text-brand-inkMuted">Remaining {currency} {o.remaining_after.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <span className="font-body text-foreground text-xs sm:text-sm whitespace-nowrap">{currency} {o.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </li>
              ))}
            </ul>
          )}
          {breakdown.total_late_deduction > 0 && (
            <p className="mt-2 text-[10px] text-brand-inkMuted">
              Late deductions of {currency} {breakdown.total_late_deduction.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} are already applied inside Income above.
            </p>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ManualAdjustmentCard
          tone="green"
          title="Manual wage adjustment — Additional"
          amount={manualAddAmount}
          reason={manualAddReason}
          onAmountChange={setManualAddAmount}
          onReasonChange={setManualAddReason}
          currency={currency}
          testIdPrefix="manual-add"
          statusLabel={adjLoading ? "Loading..." : (adjSaving ? "Saving..." : "Saved")}
        />
        <ManualAdjustmentCard
          tone="red"
          title="Manual wage adjustment — Deduction"
          amount={manualDedAmount}
          reason={manualDedReason}
          onAmountChange={setManualDedAmount}
          onReasonChange={setManualDedReason}
          currency={currency}
          testIdPrefix="manual-ded"
          statusLabel={adjLoading ? "Loading..." : (adjSaving ? "Saving..." : "Saved")}
        />
      </div>

      <div className="bg-[#1976D2]/5 border border-[#1976D2]/20 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
        <div data-testid="gross-pay">
          <p className="text-[10px] font-heading text-brand-inkMuted tracking-wider">GROSS PAY</p>
          <p className="font-heading text-lg sm:text-xl font-semibold text-foreground">
            {currency} {adjustedGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-[10px] text-brand-inkMuted mt-0.5 leading-tight">
            Income {currency} {breakdown.total_income.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {(breakdown.total_undertime || 0) > 0 && <> + UT {currency} {(breakdown.total_undertime || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>}
            {breakdown.total_overtime > 0 && <> + OT {currency} {breakdown.total_overtime.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>}
            {manualAdd > 0 && <> + Manual {currency} {manualAdd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>}
          </p>
        </div>
        <div data-testid="total-deductions">
          <p className="text-[10px] font-heading text-brand-inkMuted tracking-wider">DEDUCTIONS</p>
          <p className="font-heading text-lg sm:text-xl font-semibold text-foreground">
            − {currency} {adjustedDeductions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-[10px] text-brand-inkMuted mt-0.5">
            Module + other{manualDed > 0 && <> + Manual {currency} {manualDed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>}
          </p>
        </div>
        <div data-testid="net-pay">
          <p className="text-[10px] font-heading text-brand-inkMuted tracking-wider">NET PAY</p>
          <p className="font-heading text-2xl font-semibold text-[#1976D2]">
            {currency} {adjustedNet.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {isPaid ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F5E9] text-[#2E7D32] px-2.5 py-0.5 text-[11px] font-semibold" data-testid="paid-badge">
                <CheckCircle2 className="w-3.5 h-3.5" /> Paid · {paidStatus.date}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF3E0] text-[#E65100] px-2.5 py-0.5 text-[11px] font-semibold" data-testid="unpaid-badge">
                Unpaid
              </span>
            )}
            <Button
              type="button"
              size="sm"
              onClick={() => setMarkPaidOpen(true)}
              className={`rounded-full h-8 px-3 text-xs ${isPaid ? "bg-white border border-brand-purple/40 text-brand-purple hover:bg-brand-purpleLight/50" : "bg-[#2E7D32] hover:bg-[#1B5E20] text-white"}`}
              data-testid="mark-paid-btn"
            >
              {isPaid ? "Edit payment" : "Save & Mark as Paid"}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setPrintPreviewOpen(true)}
              className="rounded-full h-8 px-3 text-xs bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white"
              data-testid="print-slip-btn"
            >
              <Printer className="w-3.5 h-3.5 mr-1" /> Print Slip
            </Button>
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="text-[11px] font-semibold uppercase tracking-wider text-brand-purpleHover hover:text-brand-purple ml-1"
              data-testid="view-history-btn"
            >
              View History
            </button>
          </div>
        </div>
      </div>
      <MarkPaidDialog
        open={markPaidOpen}
        onOpenChange={(o) => { setMarkPaidOpen(o); if (!o) refreshPaidStatus(); }}
        branchId={branchId}
        employeeId={employeeId}
        employeeName={employeeName}
        year={year}
        month={month}
        period={period}
        netPay={adjustedNet}
        currency={currency}
        snapshot={preview}
      />
      <PayslipHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        branchId={branchId}
        employeeId={employeeId}
        employeeName={employeeName}
        currency={currency}
      />
      {printPreviewOpen && (
        <EmployeePrintPreview
          school={school}
          branch={branch}
          user={user}
          employee={preview.employee}
          year={year}
          month={month}
          period={period}
          summary={preview.summary}
          daily={filteredDaily}
          breakdown={breakdown}
          manualAdd={manualAdd}
          manualAddReason={manualAddReason}
          manualDed={manualDed}
          manualDedReason={manualDedReason}
          adjustedGross={adjustedGross}
          adjustedDeductions={adjustedDeductions}
          adjustedNet={adjustedNet}
          currency={currency}
          paidStatus={paidStatus}
          onClose={() => setPrintPreviewOpen(false)}
        />
      )}
    </section>
  );
}

function ManualAdjustmentCard({ tone, title, amount, reason, onAmountChange, onReasonChange, currency, testIdPrefix, statusLabel }) {
  const accent = tone === "green"
    ? { border: "border-[#2E7D32]/30", title: "text-[#2E7D32]", chip: "border-[#2E7D32]/40" }
    : { border: "border-[#C62828]/30", title: "text-[#C62828]", chip: "border-[#C62828]/40" };
  return (
    <div className={`bg-white rounded-2xl border ${accent.border} shadow-sm p-4`} data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className={`font-heading text-xs sm:text-sm font-semibold tracking-wider ${accent.title}`}>{title}</h3>
        {statusLabel && (
          <span
            className={`text-[10px] font-heading uppercase tracking-wider px-2 py-0.5 rounded-full border ${
              statusLabel === "Saved"
                ? "border-[#2E7D32]/30 text-[#2E7D32] bg-[#E8F5E9]"
                : "border-brand-purple/30 text-brand-purple bg-brand-purpleLight/40"
            }`}
            data-testid={`${testIdPrefix}-status`}
          >
            {statusLabel}
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        <div className="sm:col-span-1">
          <label className="text-[10px] font-heading text-brand-inkMuted">Amount</label>
          <div className={`mt-1 flex items-stretch rounded-xl border ${accent.chip} bg-white overflow-hidden`}>
            <span className={`px-2 flex items-center text-xs font-semibold ${accent.title}`}>{currency}</span>
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(e) => onAmountChange(e.target.value)}
              placeholder="0.00"
              className="h-8 border-0 rounded-none text-right font-body text-sm focus-visible:ring-0"
              data-testid={`${testIdPrefix}-amount`}
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] font-heading text-brand-inkMuted">Reason</label>
          <Input
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="Reason for this adjustment"
            className="mt-1 h-8 rounded-xl border-brand-purple/15 bg-white text-sm"
            data-testid={`${testIdPrefix}-reason`}
          />
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center" data-testid="payslip-empty">
      <p className="font-heading font-semibold text-foreground">Pick an employee and period</p>
      <p className="text-brand-inkMuted text-sm mt-1">Choose a branch, employee, month and pay period to generate the pay slip.</p>
    </div>
  );
}

function NoBranchCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1">Pay slips are scoped per branch. Use the dropdown in the header.</p>
    </div>
  );
}


function DailyIncomeBreakdownTable({ preview, currency }) {
  const dayLines = preview.day_lines || [];
  // Only daily-income labels appear in day_lines. Preserve their order from breakdown.incomes.
  const dailyIncomeLabels = (preview.breakdown.incomes || []).filter((i) => i.schedule === "Calculated Daily");
  const labelOrder = dailyIncomeLabels.map((l) => l.label_id);
  const labelNameById = Object.fromEntries(dailyIncomeLabels.map((l) => [l.label_id, l.label]));

  // Pivot: rows[date] = { date, weekday, day_type_label, attendance_label, undertime_amount, perLabel: {label_id: final_amount}, overtime, late }
  const rowsMap = {};
  for (const d of preview.daily || []) {
    rowsMap[d.date] = {
      date: d.date,
      weekday: d.weekday,
      day_type_label: d.day_type_label,
      day_type: d.day_type,
      attendance_label: d.attendance_label,
      attendance_type: d.attendance_type,
      undertime: 0,
      perLabel: {},
      overtime: 0,
      late: 0,
    };
  }
  for (const line of dayLines) {
    const row = rowsMap[line.date];
    if (!row) continue;
    row.perLabel[line.label_id] = (row.perLabel[line.label_id] || 0) + line.final_amount;
    row.overtime += line.overtime_amount || 0;
    row.undertime += line.undertime_amount || 0;
    row.late += line.late_deduction || 0;
  }
  const rows = Object.values(rowsMap).sort((a, b) => (a.date < b.date ? -1 : 1));

  // Per-column totals
  const totalsPerLabel = {};
  labelOrder.forEach((id) => { totalsPerLabel[id] = 0; });
  let otTotal = 0; let lateTotal = 0; let utTotal = 0;
  for (const r of rows) {
    labelOrder.forEach((id) => { totalsPerLabel[id] += r.perLabel[id] || 0; });
    otTotal += r.overtime; lateTotal += r.late; utTotal += r.undertime;
  }

  if (labelOrder.length === 0) {
    return null;
  }

  const fmt = (v) => `${currency} ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="payslip-daily-breakdown">
      <table className="w-full text-xs sm:text-sm table-fixed">
        <thead className="bg-[#F1F5FF]">
          <tr className="text-brand-inkMuted">
            <th className="text-left px-3 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">DATE / DAY</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">DAY TYPE</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">ATTENDANCE TYPE</th>
            {labelOrder.map((id) => (
              <th key={id} className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px] leading-tight">
                {labelNameById[id]?.toUpperCase()}
              </th>
            ))}
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">UNDERTIME</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px]">OVERTIME</th>
            <th className="text-center px-2 py-2.5 font-heading font-semibold tracking-wider text-[10px] sm:text-[11px] leading-tight">LATE DEDUCTION</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.date} className={i % 2 ? "bg-white" : "bg-[#FAFBFE]"} data-testid={`daily-breakdown-row-${r.date}`}>
              <td className="px-3 py-2 font-body text-foreground text-xs sm:text-sm">
                {formatRowDate(r.date)}<span className="hidden sm:inline"> - {r.weekday}</span>
              </td>
              <td className="px-2 py-2 text-center">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold ${DAY_TYPE_BADGE[r.day_type] || "bg-brand-purpleLight text-foreground"}`}>
                  {r.day_type_label}
                </span>
              </td>
              <td className="px-2 py-2 text-center">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold ${ATTENDANCE_BADGE[r.attendance_type] || "bg-brand-purpleLight text-foreground"}`}>
                  {r.attendance_label}
                </span>
              </td>
              {labelOrder.map((id) => (
                <td key={id} className="px-2 py-2 text-center font-body">
                  {fmt(r.perLabel[id])}
                </td>
              ))}
              <td className="px-2 py-2 text-center font-body">{fmt(r.undertime)}</td>
              <td className="px-2 py-2 text-center font-body">{fmt(r.overtime)}</td>
              <td className={`px-2 py-2 text-center font-body ${r.late > 0 ? "text-[#C62828]" : ""}`}>{fmt(r.late)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-[#F1F4FB] font-heading font-semibold text-foreground">
            <td className="px-3 py-2.5 text-left" colSpan={3}>TOTAL</td>
            {labelOrder.map((id) => (
              <td key={id} className="px-2 py-2.5 text-center">{fmt(totalsPerLabel[id])}</td>
            ))}
            <td className="px-2 py-2.5 text-center">{fmt(utTotal)}</td>
            <td className="px-2 py-2.5 text-center text-[#2E7D32]">{fmt(otTotal)}</td>
            <td className="px-2 py-2.5 text-center text-[#C62828]">{fmt(lateTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}


/* -----------------------------------------------------------------------------
 * Pay Slip Print Preview (A4 portrait, branded header + signature footer)
 * -------------------------------------------------------------------------- */
const PRINT_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function EmployeePrintPreview({
  school, branch, user, employee, year, month, period,
  summary, daily, breakdown, manualAdd, manualAddReason,
  manualDed, manualDedReason, adjustedGross, adjustedDeductions, adjustedNet,
  currency, paidStatus, onClose,
}) {
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const periodLabel =
    period === "first_half" ? "1st Half (1–15)" :
    period === "second_half" ? "2nd Half (16–End)" : "Monthly";
  const coveredDates = `${PRINT_MONTHS[month - 1]} ${
    period === "first_half" ? "1" : period === "second_half" ? "16" : "1"
  }, ${year} to ${PRINT_MONTHS[month - 1]} ${
    period === "first_half" ? "15" : new Date(year, month, 0).getDate()
  }, ${year}`;

  const isOwnerPrinting = user?.role === "school_owner" || user?.role === "sub_owner";
  // The lower-left "BRANCH ADMIN" signature should show the actual assigned
  // branch admin (configured under Owner → Branch Admins page), not the user
  // who happens to be printing — they're often different people (e.g. the
  // school owner is the one printing but the branch is managed by a separate
  // branch_admin staff member). Fallback chain: branch.branch_admin_name →
  // current user if THEY are a branch_admin → blank line for handwritten sig.
  const branchAdminName =
    branch?.branch_admin_name ||
    (user?.role === "branch_admin" ? (user?.name || "") : "");
  const ownerName = school?.owner_name || (isOwnerPrinting ? (user?.name || "") : "");

  const incomes = breakdown.incomes || [];
  const deductions = breakdown.deductions || [];
  const otherDeductions = breakdown.other_deductions || [];

  const doPrint = () => { window.print(); };

  return createPortal(
    <div className="ps-pp-overlay" id="ps-print-portal" data-testid="payslip-print-preview-overlay">
      <div className="ps-pp-toolbar no-print">
        <div className="ps-pp-toolbar-title">Print Preview · A4 Portrait</div>
        <div className="ps-pp-toolbar-actions">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-2xl border-brand-purple/30 text-foreground hover:bg-brand-purpleLight h-10 px-5"
            data-testid="payslip-print-preview-cancel"
          >
            <X className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
          <Button
            onClick={doPrint}
            className="rounded-2xl bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white h-10 px-5"
            data-testid="payslip-print-preview-print"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </div>
      </div>

      <div className="ps-pp-scroll">
        <div id="ps-print-page" className="ps-print-page" data-testid="payslip-print-page">
          <header className="ps-print-header">
            {school?.logo_base64 ? (
              <img src={school.logo_base64} alt="" className="ps-print-logo" />
            ) : (
              <div className="ps-print-logo-fallback" aria-hidden />
            )}
            <div className="ps-print-titles">
              <div className="ps-print-school">{school?.name || ""}</div>
              <div className="ps-print-branch">{branch?.name || ""}</div>
              <div className="ps-print-title">PAY SLIP</div>
              <div className="ps-print-period">{PRINT_MONTHS[month - 1]} {year} · {periodLabel}</div>
              <div className="ps-print-covered">Covered Dates: {coveredDates}</div>
            </div>
          </header>

          <div className="ps-print-empbox">
            <div>
              <div className="ps-print-empbox-label">EMPLOYEE</div>
              <div className="ps-print-empbox-name">{employee?.name || ""}</div>
              {employee?.position && (
                <div className="ps-print-empbox-sub">{employee.position}</div>
              )}
            </div>
          </div>

          <h3 className="ps-print-section-title">ATTENDANCE SUMMARY</h3>
          <table className="ps-print-summary">
            <tbody>
              <tr>
                <th>Total Days</th><td>{summary.total_days}</td>
                <th>Worked Full Shift</th><td>{summary.worked_full_shift}</td>
                <th>Days Late</th><td>{summary.late_days}</td>
                <th>Days Absent</th><td>{summary.absent_days}</td>
              </tr>
              <tr>
                <th>Normal Days</th><td>{summary.normal_total} / {summary.normal_worked}</td>
                <th>Regular Holiday</th><td>{summary.regular_holiday_total} / {summary.regular_holiday_worked}</td>
                <th>Special Holiday</th><td>{summary.special_holiday_total} / {summary.special_holiday_worked}</td>
                <th>Rest Days</th><td>{summary.rest_day_total} / {summary.rest_day_worked}</td>
              </tr>
              <tr>
                <th>Leave w/ Pay</th><td>{summary.leave_with_pay_days}</td>
                <th>Leave w/o Pay</th><td>{summary.leave_without_pay_days}</td>
                <th>OT (Normal)</th><td>{summary.overtime_normal}</td>
                <th>OT (Total)</th><td>{summary.overtime_total}</td>
              </tr>
            </tbody>
          </table>

          <h3 className="ps-print-section-title">DAILY ATTENDANCE</h3>
          <table className="ps-print-table">
            <thead>
              <tr>
                <th className="ps-pt-l">DATE / DAY</th>
                <th>DAY TYPE</th>
                <th>ATTENDANCE</th>
                <th>MIN LATE</th>
                <th>HOURS</th>
                <th>OT</th>
                <th>UT</th>
              </tr>
            </thead>
            <tbody>
              {daily.length === 0 ? (
                <tr><td colSpan={7} className="ps-pt-empty">No attendance records in this period.</td></tr>
              ) : daily.map((r) => (
                <tr key={`p-${r.date}`}>
                  <td className="ps-pt-l">{r.date} · {r.weekday?.slice(0, 3)}</td>
                  <td>{r.day_type_label}</td>
                  <td>{r.attendance_label}</td>
                  <td>{r.minutes_late}</td>
                  <td>{r.hours_worked}</td>
                  <td>{r.overtime}</td>
                  <td>{r.undertime}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="ps-print-twocol">
            <div className="ps-print-col">
              <h3 className="ps-print-section-title ps-print-income-title">INCOME</h3>
              <table className="ps-print-table">
                <thead>
                  <tr><th className="ps-pt-l">DESCRIPTION</th><th>AMOUNT</th></tr>
                </thead>
                <tbody>
                  {incomes.map((i) => (
                    <tr key={`pinc-${i.label_id}`}>
                      <td className="ps-pt-l">{i.label}</td>
                      <td>{fmt(i.amount)}</td>
                    </tr>
                  ))}
                  {breakdown.total_overtime > 0 && (
                    <tr>
                      <td className="ps-pt-l">Overtime Pay</td>
                      <td>{fmt(breakdown.total_overtime)}</td>
                    </tr>
                  )}
                  {manualAdd > 0 && (
                    <tr>
                      <td className="ps-pt-l">Manual addition{manualAddReason ? ` — ${manualAddReason}` : ""}</td>
                      <td>{fmt(manualAdd)}</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="ps-pt-l ps-pt-foot">TOTAL INCOME</td>
                    <td className="ps-pt-foot">{fmt(adjustedGross)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="ps-print-col">
              <h3 className="ps-print-section-title ps-print-ded-title">DEDUCTIONS</h3>
              <table className="ps-print-table">
                <thead>
                  <tr><th className="ps-pt-l">DESCRIPTION</th><th>AMOUNT</th></tr>
                </thead>
                <tbody>
                  {deductions.map((d) => (
                    <tr key={`pded-${d.label_id}`}>
                      <td className="ps-pt-l">{d.label}</td>
                      <td>{fmt(d.amount)}</td>
                    </tr>
                  ))}
                  {otherDeductions.map((o) => (
                    <tr key={`poth-${o.deduction_id}`}>
                      <td className="ps-pt-l">{o.name} <span className="ps-pt-sub">(remaining {fmt(o.remaining_after)})</span></td>
                      <td>{fmt(o.amount)}</td>
                    </tr>
                  ))}
                  {manualDed > 0 && (
                    <tr>
                      <td className="ps-pt-l">Manual deduction{manualDedReason ? ` — ${manualDedReason}` : ""}</td>
                      <td>{fmt(manualDed)}</td>
                    </tr>
                  )}
                  {(deductions.length === 0 && otherDeductions.length === 0 && manualDed === 0) && (
                    <tr><td colSpan={2} className="ps-pt-empty">No deductions this period.</td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="ps-pt-l ps-pt-foot">TOTAL DEDUCTIONS</td>
                    <td className="ps-pt-foot">{fmt(adjustedDeductions)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="ps-print-net">
            <div className="ps-print-net-row">
              <span>GROSS PAY</span>
              <span className="ps-print-net-val">{fmt(adjustedGross)}</span>
            </div>
            <div className="ps-print-net-row">
              <span>TOTAL DEDUCTIONS</span>
              <span className="ps-print-net-val">− {fmt(adjustedDeductions)}</span>
            </div>
            <div className="ps-print-net-row ps-print-net-final">
              <span>NET PAY</span>
              <span className="ps-print-net-val ps-print-net-final-val">{fmt(adjustedNet)}</span>
            </div>
          </div>

          <div className="ps-print-sig-wrap">
            <div className="ps-print-sig">
              <div className="ps-print-sig-name">{branchAdminName || "\u00A0"}</div>
              <div className="ps-print-sig-line" />
              <div className="ps-print-sig-title">BRANCH ADMIN</div>
            </div>
            <div className="ps-print-sig">
              <div className="ps-print-sig-name">{employee?.name || "\u00A0"}</div>
              <div className="ps-print-sig-line" />
              <div className="ps-print-sig-title">RECEIVED BY (EMPLOYEE)</div>
            </div>
            <div className="ps-print-sig">
              <div className="ps-print-sig-name">{ownerName || "\u00A0"}</div>
              <div className="ps-print-sig-line" />
              <div className="ps-print-sig-title">SCHOOL OWNER</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ps-pp-overlay {
          position: fixed; inset: 0; z-index: 60;
          background: rgba(15, 23, 42, 0.55);
          display: flex; flex-direction: column;
        }
        .ps-pp-toolbar {
          display: flex; align-items: center; justify-content: space-between;
          gap: 1rem; padding: 0.75rem 1.25rem;
          background: #fff;
          border-bottom: 1px solid rgba(37, 99, 235, 0.12);
          box-shadow: 0 4px 12px -8px rgba(15, 23, 42, 0.25);
        }
        .ps-pp-toolbar-title {
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 0.95rem; color: #0f172a;
        }
        .ps-pp-toolbar-actions { display: flex; gap: 0.5rem; }
        .ps-pp-scroll {
          flex: 1; overflow: auto; padding: 1.5rem; display: flex; justify-content: center;
        }
        .ps-print-page {
          width: 190mm; min-height: 277mm; background: #fff;
          padding: 4mm 10mm 8mm 10mm;
          box-shadow: 0 8px 32px -8px rgba(15, 23, 42, 0.35);
          color: #0f172a;
          font-family: var(--font-body), sans-serif;
        }
        .ps-print-header {
          display: flex; align-items: center; justify-content: center;
          gap: 10mm; margin-bottom: 4mm; text-align: center;
          border-bottom: 1.5pt solid #0f172a; padding-bottom: 3mm;
        }
        .ps-print-logo { width: 20mm; height: 20mm; object-fit: contain; flex-shrink: 0; }
        .ps-print-logo-fallback {
          width: 20mm; height: 20mm; flex-shrink: 0;
          background: linear-gradient(135deg, #DBEAFE, #BFDBFE);
          border-radius: 6mm;
        }
        .ps-print-titles { display: flex; flex-direction: column; gap: 1mm; }
        .ps-print-school {
          font-family: var(--font-heading), inherit;
          font-weight: 800; font-size: 16pt; letter-spacing: 0.02em;
          color: #0f172a; text-transform: uppercase;
        }
        .ps-print-branch {
          font-family: var(--font-heading), inherit;
          font-weight: 600; font-size: 11pt; color: #334155;
        }
        .ps-print-title {
          font-family: var(--font-heading), inherit;
          font-weight: 800; font-size: 14pt; letter-spacing: 0.08em;
          color: #2563eb; margin-top: 1mm; text-transform: uppercase;
        }
        .ps-print-period { font-size: 9.5pt; font-weight: 600; color: #0f172a; margin-top: 0.5mm; }
        .ps-print-covered { font-size: 8.5pt; color: #475569; }

        .ps-print-empbox {
          display: flex; justify-content: space-between; align-items: center;
          background: #EEF3FF; border: 0.75pt solid #475569;
          padding: 2.5mm 3.5mm; margin: 3mm 0 4mm;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        .ps-print-empbox-right { text-align: right; }
        .ps-print-empbox-label {
          font-family: var(--font-heading), inherit;
          font-size: 7pt; font-weight: 700; letter-spacing: 0.08em;
          color: #475569; text-transform: uppercase;
        }
        .ps-print-empbox-name {
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 12pt; color: #0f172a;
          text-transform: uppercase;
        }
        .ps-print-empbox-sub { font-size: 8.5pt; color: #475569; }
        .ps-print-empbox-status {
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 11pt;
          padding: 0.5mm 3mm; border-radius: 3mm; display: inline-block;
        }
        .ps-print-empbox-status.is-paid { background: #DCFCE7; color: #166534; }
        .ps-print-empbox-status.is-unpaid { background: #FFEDD5; color: #9A3412; }

        .ps-print-section-title {
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 9.5pt;
          letter-spacing: 0.08em;
          color: #0f172a; text-transform: uppercase;
          margin: 4mm 0 1.5mm;
          padding-bottom: 0.5mm;
          border-bottom: 0.5pt solid #cbd5e1;
        }
        .ps-print-income-title { color: #047857; border-bottom-color: #86efac; }
        .ps-print-ded-title { color: #b91c1c; border-bottom-color: #fca5a5; }

        .ps-print-summary {
          width: 100%; border-collapse: collapse;
          font-size: 8pt;
          font-variant-numeric: tabular-nums;
          margin-bottom: 2mm;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        .ps-print-summary th, .ps-print-summary td {
          border: 0.5pt solid #94a3b8;
          padding: 1.2mm 2mm;
          text-align: left; vertical-align: middle;
          white-space: nowrap;
        }
        .ps-print-summary th {
          background: #F6F8FE;
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 7.5pt;
          letter-spacing: 0.02em; color: #475569;
          text-transform: uppercase; width: 12%;
        }
        .ps-print-summary td { font-weight: 600; color: #0f172a; width: 13%; }

        .ps-print-table {
          width: 100%; border-collapse: collapse;
          font-size: 8pt; font-variant-numeric: tabular-nums;
          margin-bottom: 2mm;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        .ps-print-table th, .ps-print-table td {
          border: 0.6pt solid #475569;
          padding: 1.2mm 2mm;
          text-align: center; vertical-align: middle;
          white-space: nowrap;
        }
        .ps-print-table thead th {
          background: #EEF3FF;
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 7.5pt;
          color: #0f172a; letter-spacing: 0.02em;
        }
        .ps-pt-l { text-align: left !important; white-space: normal !important; word-break: break-word; }
        .ps-pt-foot {
          background: #E2EAFB;
          font-family: var(--font-heading), inherit;
          font-weight: 700;
        }
        .ps-pt-empty {
          text-align: center !important; color: #94a3b8;
          font-style: italic; padding: 3mm !important;
        }
        .ps-pt-sub { font-size: 7pt; color: #64748b; font-weight: 400; }

        .ps-print-twocol {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 4mm; margin-top: 2mm;
        }
        .ps-print-col { min-width: 0; }

        .ps-print-net {
          margin-top: 4mm; padding: 3mm 4mm;
          border: 0.75pt solid #2563eb;
          background: #EFF6FF;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        .ps-print-net-row {
          display: flex; justify-content: space-between; align-items: baseline;
          font-size: 9pt; font-weight: 600;
          padding: 0.5mm 0;
        }
        .ps-print-net-val { font-variant-numeric: tabular-nums; }
        .ps-print-net-final {
          margin-top: 1mm; padding-top: 1.5mm;
          border-top: 0.6pt solid #2563eb;
          font-size: 12pt;
        }
        .ps-print-net-final-val {
          font-family: var(--font-heading), inherit;
          font-weight: 800; color: #2563eb; font-size: 14pt;
        }

        .ps-print-sig-wrap {
          display: flex; justify-content: space-between;
          margin-top: 20mm; gap: 6mm;
        }
        .ps-print-sig { flex: 1; text-align: center; }
        .ps-print-sig-name {
          font-family: var(--font-heading), inherit;
          font-weight: 700; font-size: 9.5pt;
          padding: 0 2mm 0.5mm 2mm;
          text-transform: uppercase;
          min-height: 5mm;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .ps-print-sig-line { border-top: 0.75pt solid #0f172a; margin: 0 3mm; }
        .ps-print-sig-title {
          font-family: var(--font-heading), inherit;
          font-weight: 600; font-size: 7.5pt;
          letter-spacing: 0.06em;
          color: #475569; margin-top: 1mm;
          text-transform: uppercase;
        }

        @media print {
          @page { size: A4 portrait; margin: 8mm; }
          /* The print preview is portaled to <body>. Hide every other body
             child so we don't generate empty pages from the (now hidden) app
             shell taking layout space behind us. */
          body > *:not(#ps-print-portal) { display: none !important; }
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; height: auto !important; }
          #ps-print-portal {
            position: static !important;
            background: #fff !important;
            display: block !important;
            inset: auto !important;
            z-index: auto !important;
          }
          .no-print { display: none !important; }
          .ps-pp-scroll {
            padding: 0 !important; overflow: visible !important;
            display: block !important;
            height: auto !important;
          }
          .ps-print-page {
            width: auto !important;
            min-height: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .ps-print-table tr, .ps-print-summary tr { page-break-inside: avoid; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          .ps-print-sig-wrap { page-break-inside: avoid; margin-top: 14mm !important; }
          .ps-print-table th, .ps-print-table td,
          .ps-print-summary th, .ps-print-summary td {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>,
    document.body
  );
}
